/* eslint-disable prettier/prettier */
import { Flex, Icon } from '@bitrise/bitkit';
import { VizContext } from '@bitrise/bitviz';
import React, { FunctionComponent, PointerEventHandler, useCallback, useContext, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import './BuildTimeSelector.css';

export interface Props {
  onApply: (xLeft: number, xRight: number) => void;
  onChange: (xLeft: number, xRight: number) => void;
  xLeft: number;
  xRight: number;
}

const BuildTimeSelector: FunctionComponent<Props> = ({ onApply, onChange, xLeft, xRight }) => {
  const { artboardWidth, getX, getDomainX, ref } = useContext(VizContext);
  const refDragHandle = useRef<'left' | 'right'>();
  const refHandlePositions = useRef<[number, number]>([xLeft, xRight]);
  const refVizBoundsRect = useRef<DOMRect | null>(null);

  const handleDrag = useCallback((event: PointerEvent) => {
    const { clientX } = event;
    const [currentXLeft, currentXRight] = refHandlePositions.current;

    if (refVizBoundsRect.current && refDragHandle.current) {
      const { left } = refVizBoundsRect.current;
      const relX = Math.max(0, Math.min(clientX - left, artboardWidth));
      const nextDomainX = getDomainX(relX);

      // eslint-disable-next-line no-nested-ternary
      const [nextLeft, nextRight] = refDragHandle.current === 'left'
        ? (nextDomainX > currentXRight ? [currentXRight, nextDomainX] : [nextDomainX, currentXRight])
        : (nextDomainX < currentXLeft ? [nextDomainX, currentXLeft] : [currentXLeft, nextDomainX]);

      if (nextLeft !== currentXLeft || nextRight !== currentXRight) {
        refHandlePositions.current = [nextLeft, nextRight];
        onChange(nextLeft, nextRight);
      }
    }
  },  [artboardWidth, getDomainX, onChange]);

  const handleDragEnd = useCallback(() => {
    const [currentXLeft, currentXRight] = refHandlePositions.current;

    onApply(currentXLeft, currentXRight);

    refDragHandle.current = undefined;
    refVizBoundsRect.current = null;

    document.removeEventListener('pointermove', handleDrag);
    document.removeEventListener('pointerup', handleDragEnd);
  }, [handleDrag, onApply]);

  const handleDragStart = useCallback((handle: 'left' | 'right'): PointerEventHandler => () => {
    refVizBoundsRect.current = ref.current?.getBoundingClientRect() || null;
    refDragHandle.current = handle;

    document.addEventListener('pointerup', handleDragEnd);
    document.addEventListener('pointermove', handleDrag);
  }, [handleDrag, handleDragEnd, ref]);

  useEffect(() => {
    refHandlePositions.current = [xLeft, xRight];
  }, [xLeft, xRight]);

  const pxLeft = getX(xLeft);
  const pxRight = getX(xRight);

  if (pxLeft === null || pxRight === null) {
    return null;
  }

  const pxDiff = pxRight - pxLeft;
  const xScale = pxDiff / artboardWidth;

  return (
    <Flex className="BuildTimeSelector">
      <motion.div
        className="BuildTimeSelector__connector-container"
        animate={{ x: pxLeft, scaleX: xScale }}
      >
        <Flex className="BuildTimeSelector__connector" backgroundColor="grape-4" />
      </motion.div>

      <motion.div animate={{ x: pxLeft }} onPointerDown={handleDragStart('left')}>
        <Flex backgroundColor="grape-4" className="BuildTimeSelector__handle" elevation="x3">
          <Icon name="MenuHamburger" size="1rem" textColor="white" />
        </Flex>
      </motion.div>

      <motion.div animate={{ x: pxRight }} onPointerDown={handleDragStart('right')}>
        <Flex backgroundColor="grape-4" className="BuildTimeSelector__handle" elevation="x3">
          <Icon name="MenuHamburger" size="1rem" textColor="white" />
        </Flex>
      </motion.div>
    </Flex>
  );
};

export default BuildTimeSelector;
